import { apiClient, HttpError, isNetworkError } from '@littlebird/auth-client';
import { EventNames, globalEventBus } from '@littlebird/event-bus';
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { z } from 'zod';

import { useRefetchWhenTurnEnds } from './refetch-when-turn-ends';

const signInRequestSchema = z.object({
  input_request_id: z.string(),
  origin: z.string().url(),
  status: z.enum(['pending', 'resolved']),
  sections: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      qr_image_file_id: z.string().optional(),
      fields: z.array(
        z
          .object({
            id: z.string(),
            label: z.string(),
            type: z.enum(['text', 'password', 'email', 'redirect_url']),
            initial_value: z.string().optional(),
            max_length: z.number().int().min(1).max(8).optional(),
          })
          .transform((field) =>
            field.type === 'text' && field.max_length !== undefined
              ? {
                  id: field.id,
                  label: field.label,
                  type: 'otp' as const,
                  max_length: field.max_length,
                }
              : field
          )
      ),
      choices: z.array(
        z.object({
          id: z.string(),
          label: z.string(),
          saved_credential: z.boolean().optional(),
        })
      ),
    })
  ),
  allow_back: z.boolean(),
  result: z.record(z.string(), z.string()).nullable(),
  notified: z.boolean(),
});

const signInSessionSchema = z.object({
  sign_in_session_id: z.string(),
  chat_id: z.string(),
  origin: z.string().url(),
  expires_at: z.string(),
  status: z.enum(['active', 'success', 'failure', 'timedout']),
  profile_released: z.boolean().optional(),
  continuations_pending: z.array(z.string()).optional(),
  request: signInRequestSchema.nullable(),
});

export type SignInSession = z.infer<typeof signInSessionSchema>;
export const useSignInQrImageQuery = (fileId: string) =>
  useQuery({
    queryKey: ['sign-in-qr-image', fileId],
    queryFn: async ({ signal }) =>
      z.object({ url: z.string().url() }).parse(
        await apiClient.get(`files/${encodeURIComponent(fileId)}`, {
          signal,
          emitHttpErrorEvent: false,
        })
      ),
    retry: false,
    gcTime: 0,
  });

type SignInResponse =
  | { action: 'fill'; section_id: string; values: Record<string, string> }
  | { action: 'choose'; section_id: string; choice_id: string }
  | { action: 'back' | 'close' };

async function sendResponse({
  sessionId,
  body,
}: {
  sessionId: string;
  body: SignInResponse & { input_request_id: string };
}) {
  const response = await apiClient.fetch(
    `browser/sign_in_sessions/${encodeURIComponent(sessionId)}/respond`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Blob bodies keep private values out of the API client's error telemetry
      body: new Blob([JSON.stringify(body)], { type: 'application/json' }),
    }
  );
  if (!response.ok) throw new HttpError(response);
  return response;
}

const signInConflictSchema = z.object({
  error: z.literal('profile_sign_in_busy'),
  source: z.enum(['chat', 'messaging_channel']).optional(),
  message: z.string(),
  sign_in_session_id: z.string(),
  chat_id: z.string(),
  origin: z.string().url(),
  expires_at: z.string(),
  phase: z.string(),
});

export type SignInConflict = z.infer<typeof signInConflictSchema>;

export const parseSignInConflict = (result: string | null | undefined) => {
  if (!result) return null;
  try {
    const parsed = signInConflictSchema.safeParse(JSON.parse(result));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const sessionKey = (id: string) => ['sign-in-session', id] as const;
const chatSessionKey = (chatId: string) =>
  ['chat-sign-in-session', chatId] as const;
const dismissedRequestKey = (requestId: string | undefined) =>
  ['dismissed-sign-in-request', requestId] as const;

/** Nothing is left for the user, the agent, or delivery to do on this sign-in */
const isSignInSettled = (session: SignInSession | null | undefined) =>
  session === null ||
  (session?.profile_released === true &&
    session.request === null &&
    !session.continuations_pending?.length);

export const useSignInSessionQuery = (signInSessionId: string) =>
  useQuery({
    queryKey: sessionKey(signInSessionId),
    queryFn: async ({ signal }) => {
      try {
        return signInSessionSchema.parse(
          await apiClient.get(
            `browser/sign_in_sessions/${encodeURIComponent(signInSessionId)}`,
            {
              signal,
              emitHttpErrorEvent: false,
            }
          )
        );
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) return null;
        throw error;
      }
    },
    refetchInterval: ({ state: { data } }) =>
      isSignInSettled(data) ? false : 5_000,
    retry: false,
  });

/**
 * The chat's own sign-in. Polls while a turn runs, since a turn may start one
 * at any time, and while a started sign-in still has work left.
 */
export const useChatSignInSessionQuery = (
  chatId: string,
  enabled: boolean,
  chatBusy: boolean
) => {
  const client = useQueryClient();
  useEffect(() => {
    const subscription = globalEventBus.on(
      EventNames.CHAT_DID_STOP,
      async ({ data: { threadId, cancelledSignInRequestId } }) => {
        if (threadId !== chatId) return;
        // Discard polls started before Stop, then hide only the cancelled input
        await client.cancelQueries({ queryKey: chatSessionKey(chatId) });
        if (cancelledSignInRequestId) {
          client.setQueryData(
            dismissedRequestKey(cancelledSignInRequestId),
            true
          );
        }
        void client.invalidateQueries({ queryKey: chatSessionKey(chatId) });
      }
    );
    return () => subscription.unsubscribe();
  }, [chatId, client]);
  useRefetchWhenTurnEnds(chatSessionKey(chatId), chatBusy);
  return useQuery({
    queryKey: chatSessionKey(chatId),
    queryFn: async ({ signal }) =>
      z.object({ session: signInSessionSchema.nullable() }).parse(
        await apiClient.get('browser/sign_in_sessions', {
          params: { chat_id: chatId },
          signal,
          emitHttpErrorEvent: false,
        })
      ).session,
    enabled,
    refetchInterval: ({ state: { data } }) =>
      chatBusy || !isSignInSettled(data) ? 5_000 : false,
    retry: false,
  });
};

export const useCloseOtherSignInMutation = (chatId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (conflict: SignInConflict) => {
      await apiClient.post(
        `browser/sign_in_sessions/${encodeURIComponent(conflict.sign_in_session_id)}/close?resume_chat_id=${encodeURIComponent(chatId)}`,
        undefined,
        { emitHttpErrorEvent: false }
      );
    },
    onSettled: (_result, _error, conflict) => {
      void client.invalidateQueries({
        queryKey: sessionKey(conflict.sign_in_session_id),
      });
      void client.invalidateQueries({
        queryKey: chatSessionKey(conflict.chat_id),
      });
      void client.invalidateQueries({ queryKey: chatSessionKey(chatId) });
    },
  });
};

export const useSignInSessionActions = (
  signInSessionId: string,
  chatId: string,
  inputRequestId: string | undefined,
  expiresAt: string
) => {
  const client = useQueryClient();
  // Preserve dismissal across navigation for the remaining session lifetime
  const { data: dismissed } = useQuery({
    queryKey: dismissedRequestKey(inputRequestId),
    initialData: false,
    enabled: false,
    gcTime: Math.max(0, Date.parse(expiresAt) - Date.now()),
  });
  const responseKey = [
    'sign-in-response',
    signInSessionId,
    inputRequestId,
  ] as const;
  // Pending mutations survive unmounts; completed mutations use normal cache retention
  const responseStatus = useMutationState({
    filters: { mutationKey: responseKey, exact: true },
    select: (mutation) => mutation.state.status,
  }).at(-1);
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: sessionKey(signInSessionId) }),
      client.invalidateQueries({ queryKey: chatSessionKey(chatId) }),
    ]);
  };
  const updateSession = async (data: unknown) => {
    const session = signInSessionSchema.parse(data);
    // Discard polls that may still contain the pre-delivery notification state
    await Promise.all([
      client.cancelQueries({ queryKey: sessionKey(signInSessionId) }),
      client.cancelQueries({ queryKey: chatSessionKey(chatId) }),
    ]);
    client.setQueryData(sessionKey(signInSessionId), session);
    client.setQueryData(chatSessionKey(chatId), session);
  };
  // Mutations keep retrying after the dialog unmounts and resume on reconnect
  const close = useMutation({
    mutationFn: sendResponse,
    retry: (_failureCount, error) =>
      (error instanceof HttpError &&
        ((error.status >= 500 && error.status < 600) ||
          error.status === 408 ||
          error.status === 429)) ||
      isNetworkError(error),
    onSettled: () => void refresh().catch(() => undefined),
  });
  const response = useMutation({
    mutationKey: responseKey,
    retry: false,
    mutationFn: async ({
      body,
      preparation,
    }: {
      body: SignInResponse & { input_request_id: string };
      preparation: Promise<void>;
    }) => {
      try {
        await preparation;
        const result = await sendResponse({ sessionId: signInSessionId, body });
        await updateSession(await result.json());
      } finally {
        // Mutation history must not retain submitted credentials
        if (body.action === 'fill') body.values = {};
      }
    },
    onError: () => void refresh().catch(() => undefined),
  });
  const respond = async (
    responseBody: SignInResponse,
    beforeRespond?: () => void | Promise<void>
  ) => {
    if (!inputRequestId) return;
    const body = { ...responseBody, input_request_id: inputRequestId };
    // Release browser control synchronously, before the mutation starts
    const preparation = new Promise<void>((resolve) =>
      resolve(beforeRespond?.())
    );
    if (responseBody.action === 'close') {
      client.setQueryData(dismissedRequestKey(inputRequestId), true);
      await preparation.catch(() => undefined);
      close.mutate({ sessionId: signInSessionId, body });
      return;
    }
    await response.mutateAsync({ body, preparation }).catch(() => undefined);
  };
  const resume = useMutation({
    mutationFn: async () => {
      if (!inputRequestId) return;
      const result = await apiClient.post(
        `browser/sign_in_sessions/${encodeURIComponent(signInSessionId)}/requests/${encodeURIComponent(inputRequestId)}/resume`,
        undefined,
        { emitHttpErrorEvent: false }
      );
      await updateSession(result);
    },
  });
  return {
    respond: {
      isPending: responseStatus === 'pending',
      isError: responseStatus === 'error',
      dismissed,
      submit: respond,
    },
    resume,
  };
};
